import React, { useEffect, useRef, useState } from "react";
import { BookUser, Clock3, Eye, EyeOff, Mail, Monitor, Shield, Upload, User, X } from "lucide-react";
import { getLoginHistory } from "../../utils/loginHistory";
import { memoryStorage } from "../../utils/memoryStorage";
import "./ProfileModal.css";

// Profile modal supports viewing info, editing info, and changing password.
export default function ProfileModal({ open, onClose }) {
  const fileInputRef = useRef(null);
  const [tab, setTab] = useState("profile");
  const [mode, setMode] = useState("view");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("Content Manager");
  const [email, setEmail] = useState("content@admin.com");
  const [timezone, setTimezone] = useState("Indian/Maldives");
  const [bio, setBio] = useState("-");
  const [profileImage, setProfileImage] = useState("");
  const [loginHistoryRows, setLoginHistoryRows] = useState([]);

  useEffect(() => {
    if (!open) return;
    let storedUser = null;
    try {
      const storedUserRaw = memoryStorage.getItem("user");
      storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : null;
    } catch {
      storedUser = null;
    }
    setLoginHistoryRows(getLoginHistory(storedUser).slice(0, 5));
  }, [open]);

  // Do not render the modal until it is opened.
  if (!open) return null;

  const openImagePicker = () => fileInputRef.current?.click();

  // Converts the selected image into a preview the modal can display immediately.
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") setProfileImage(reader.result);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // Reused upload UI for both profile view and edit states.
  const renderUploadCircle = () => (
    <div
      className={`cm-profile-upload ${profileImage ? "has-image" : ""}`}
      role="button"
      tabIndex={0}
      onClick={openImagePicker}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openImagePicker();
        }
      }}
    >
      {profileImage ? (
        <img src={profileImage} alt="Profile" className="cm-profile-upload-preview" />
      ) : (
        <>
          <Upload size={22} />
          <p>Upload</p>
          <small>Accepts .jpg, .jpeg, .png files</small>
        </>
      )}
    </div>
  );

  return (
    <div className="cm-profile-overlay" onClick={onClose}>
      <div className="cm-profile-modal" onClick={(e) => e.stopPropagation()}>
        <input
          ref={fileInputRef}
          className="cm-hidden-input"
          type="file"
          accept="image/png,image/jpeg"
          onChange={handleImageUpload}
        />
        <div className="cm-profile-head">
          <h3>{mode === "change-password" ? "Change Password" : "My Profile"}</h3>
          <button type="button" className="cm-profile-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {mode === "view" && (
          <>
            <div className="cm-profile-tabs">
              <button
                type="button"
                className={tab === "profile" ? "active" : ""}
                onClick={() => setTab("profile")}
              >
                Profile
              </button>
              <button
                type="button"
                className={tab === "history" ? "active" : ""}
                onClick={() => setTab("history")}
              >
                Login History
              </button>
            </div>

            {tab === "profile" ? (
              <div className="cm-profile-body">
                <h4>Account Details</h4>
                <div className="cm-profile-grid">
                  {renderUploadCircle()}
                  <div className="cm-profile-col">
                    <div className="cm-profile-row">
                      <User size={16} />
                      <div>
                        <span>Name</span>
                        <strong>{name}</strong>
                      </div>
                    </div>
                    <div className="cm-profile-row">
                      <Clock3 size={16} />
                      <div>
                        <span>Timezone</span>
                        <strong>{timezone}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="cm-profile-col">
                    <div className="cm-profile-row">
                      <Mail size={16} />
                      <div>
                        <span>Email Address</span>
                        <strong>{email}</strong>
                      </div>
                    </div>
                    <div className="cm-profile-row">
                      <BookUser size={16} />
                      <div>
                        <span>Biography</span>
                        <strong>{bio}</strong>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="cm-profile-actions">
                  <button type="button" className="cm-profile-outline" onClick={() => setMode("change-password")}>
                    Change Password
                  </button>
                  <button type="button" className="cm-profile-solid" onClick={() => setMode("edit-info")}>
                    Edit Info
                  </button>
                </div>
              </div>
            ) : (
              <div className="cm-login-history-wrap">
                <table className="cm-login-history-table">
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Device</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginHistoryRows.length ? (
                      loginHistoryRows.map((row) => (
                        <tr key={row.id}>
                          <td><Clock3 size={16} /> {row.at}</td>
                          <td><Monitor size={16} /> {row.device}</td>
                          <td className={row.ok ? "ok" : "fail"}>
                            <Shield size={16} /> {row.ok ? "Success" : "Failed"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td><Clock3 size={16} /> --</td>
                        <td><Monitor size={16} /> --</td>
                        <td className="ok">
                          <Shield size={16} /> --
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {mode === "edit-info" && (
          <div className="cm-edit-info-body">
            <div className="cm-profile-grid">
              {renderUploadCircle()}
              <div className="cm-profile-col">
                <div className="cm-edit-field">
                  <label>Name</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="cm-edit-field">
                  <label>Timezone</label>
                  <input type="text" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
                </div>
              </div>
              <div className="cm-profile-col">
                <div className="cm-edit-field">
                  <label>Email Address</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="cm-edit-field">
                  <label>Biography</label>
                  <input type="text" value={bio} onChange={(e) => setBio(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="cm-change-password-actions">
              <button type="button" className="cm-password-cancel" onClick={() => setMode("view")}>
                Cancel
              </button>
              <button type="button" className="cm-password-update" onClick={() => setMode("view")}>
                Save Changes
              </button>
            </div>
          </div>
        )}

        {mode === "change-password" && (
          <div className="cm-change-password-body">
            <label>Current Password</label>
            <div className="cm-password-input-wrap active">
              <input
                type={showCurrent ? "text" : "password"}
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <button type="button" onClick={() => setShowCurrent((prev) => !prev)}>
                {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <label>New Password</label>
            <div className="cm-password-input-wrap">
              <input
                type={showNew ? "text" : "password"}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button type="button" onClick={() => setShowNew((prev) => !prev)}>
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <label>Confirm New Password</label>
            <div className="cm-password-input-wrap">
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button type="button" onClick={() => setShowConfirm((prev) => !prev)}>
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="cm-change-password-actions">
              <button type="button" className="cm-password-cancel" onClick={() => setMode("view")}>
                Cancel
              </button>
              <button type="button" className="cm-password-update">
                Update Password
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
 